import React, { useState, useEffect } from 'react';
import { getBackups, createBackup, restoreBackup, deleteBackup, getBackupConfig, updateBackupConfig } from '../services/backupService';
import {
    Button,
    Typography,
    Alert,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    CircularProgress,
    Snackbar
} from '@mui/material';
import CreateBackupModal from '../components/CreateBackupModal';
import ConfigBackupModal from '../components/ConfigBackupModal';
import RestoringOverlay from '../components/RestoringOverlay';
import PageHeader from '../components/ui/PageHeader';
import '../style/pages/GestionBackups.css';

const GestionBackups = () => {
    const [backups, setBackups] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [selectedBackup, setSelectedBackup] = useState(null);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showConfigModal, setShowConfigModal] = useState(false);
    const [backupConfig, setBackupConfig] = useState(null);
    const [showRestoreModal, setShowRestoreModal] = useState(false);
    const [isRestoring, setIsRestoring] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deletingBackup, setDeletingBackup] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const fetchBackups = async () => {
        setLoading(true);
        try {
            const response = await getBackups();
            setBackups(response.data);
            setError(null);
        } catch (error) {
            setError('Error al cargar los backups. Por favor, inténtelo de nuevo más tarde.');
            console.error('Error fetching backups:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchBackups();
    }, []);

    const handleOpenConfig = async () => {
        setLoading(true);
        try {
            const response = await getBackupConfig();
            setBackupConfig(response.data);
            setShowConfigModal(true);
        } catch (error) {
            setSnackbar({
                open: true,
                message: 'Error al obtener configuración de backups',
                severity: 'error'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleSaveConfig = async (config) => {
        setLoading(true);
        try {
            const response = await updateBackupConfig(config);
            setSnackbar({ open: true, message: response.data.message, severity: 'success' });
            setShowConfigModal(false);
            // Optionally refresh config if needed, but we just saved it.
        } catch (error) {
            setSnackbar({
                open: true,
                message: 'Error al actualizar configuración',
                severity: 'error'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleCreateBackup = async (backupName) => {
        setLoading(true);
        try {
            const response = await createBackup(backupName);
            setSnackbar({ open: true, message: response.data.message, severity: 'success' });
            setShowCreateModal(false);
            fetchBackups();
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Error al crear el backup.';
            setError(errorMessage);
            console.error('Error creating backup:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleRestoreBackup = async () => {
        if (selectedBackup) {
            setShowRestoreModal(false);
            setIsRestoring(true);

            try {
                await restoreBackup(selectedBackup.filename);
            } catch (error) {
                console.warn('Se inició la restauración. El servidor se está reiniciando. Empezando sondeo...', error);
            } finally {
                const pollInterval = setInterval(async () => {
                    try {
                        console.log('Sondeando estado del backend...');
                        await getBackups();

                        console.log('¡El backend está en línea!');
                        clearInterval(pollInterval);

                        setIsRestoring(false);
                        setSnackbar({ open: true, message: 'Restauración completada y sistema reiniciado con éxito.', severity: 'success' });

                        setTimeout(() => window.location.reload(), 2000);
                    } catch (pollError) {
                        console.log('El backend sigue fuera de línea, reintentando...');
                    }
                }, 3000);
            }
        }
    };

    const openRestoreModal = (backup) => {
        setSelectedBackup(backup);
        setShowRestoreModal(true);
    };

    const closeRestoreModal = () => {
        setSelectedBackup(null);
        setShowRestoreModal(false);
    };

    const openDeleteModal = (backup) => {
        setDeletingBackup(backup);
        setShowDeleteModal(true);
    };

    const closeDeleteModal = () => {
        setDeletingBackup(null);
        setShowDeleteModal(false);
    };

    const handleDeleteBackup = async () => {
        if (!deletingBackup) return;
        closeDeleteModal();
        setLoading(true);
        try {
            const response = await deleteBackup(deletingBackup.filename);
            setSnackbar({ open: true, message: response.data.message || 'Backup eliminado exitosamente.', severity: 'success' });
            fetchBackups();
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Error al eliminar el backup.';
            setSnackbar({ open: true, message: errorMessage, severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleCloseSnackbar = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setSnackbar({ ...snackbar, open: false });
    };

    if (loading && backups.length === 0) {
        return (
            <div className="text-center my-5">
                <div className="spinner-border text-primary" style={{ width: "3rem", height: "3rem" }}></div>
                <p className="mt-3 fw-semibold">Cargando backups...</p>
            </div>
        );
    }

    return (
        <div className="area-admin">
            <RestoringOverlay show={isRestoring} />

            <div className="card border-0" style={{ borderRadius: '12px', boxShadow: 'var(--shadow-sm)' }}>
                <div className="card-body p-0">
                    <PageHeader
                        variante="banner"
                        icono="database-fill"
                        titulo="Gestión de Backups"
                        descripcion="Copias de seguridad y restauración de la base de datos"
                        accion={
                            <div className="d-flex gap-2 flex-wrap">
                                <button
                                    className="btn btn-light d-flex align-items-center gap-2 px-3 py-2"
                                    onClick={handleOpenConfig}
                                    style={{
                                        borderRadius: '8px',
                                        fontWeight: '600',
                                        fontFamily: 'var(--font-family-base)',
                                        boxShadow: 'var(--shadow-sm)'
                                    }}
                                >
                                    <i className="bi bi-gear-fill"></i>
                                    Configuración
                                </button>
                                <button
                                    className="btn btn-light d-flex align-items-center gap-2 px-3 py-2"
                                    onClick={() => setShowCreateModal(true)}
                                    style={{
                                        borderRadius: '8px',
                                        fontWeight: '600',
                                        fontFamily: 'var(--font-family-base)',
                                        boxShadow: 'var(--shadow-sm)'
                                    }}
                                >
                                    <i className="bi bi-plus-circle-fill"></i>
                                    Crear Backup
                                </button>
                                <button
                                    className="btn btn-outline-light d-flex align-items-center gap-2 px-3 py-2"
                                    onClick={fetchBackups}
                                    disabled={loading}
                                    style={{
                                        borderRadius: '8px',
                                        fontWeight: '600',
                                        fontFamily: 'var(--font-family-base)'
                                    }}
                                >
                                    {loading ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                            Recargando...
                                        </>
                                    ) : (
                                        <>
                                            <i className="bi bi-arrow-clockwise"></i>
                                            Recargar
                                        </>
                                    )}
                                </button>
                            </div>
                        }
                    />

                    {/* Mensaje de error */}
                    {error && (
                        <div className="px-4 mb-3">
                            <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
                        </div>
                    )}

                    {/* Tabla moderna */}
                    <div className="px-4 pb-4">
                        <div className="table-responsive">
                            <table className="table align-middle" style={{ marginBottom: 0 }}>
                                <thead>
                                    <tr style={{
                                        background: 'var(--gradient-primary-light)',
                                        borderBottom: '2px solid #3B82F6'
                                    }}>
                                        <th style={{ padding: '1rem', fontWeight: '600', color: '#1e40af', fontFamily: 'var(--font-family-base)' }}>Nombre del Archivo</th>
                                        <th style={{ padding: '1rem', fontWeight: '600', color: '#1e40af', fontFamily: 'var(--font-family-base)' }}>Tamaño</th>
                                        <th style={{ padding: '1rem', fontWeight: '600', color: '#1e40af', fontFamily: 'var(--font-family-base)' }}>Fecha de Creación</th>
                                        <th style={{ padding: '1rem', fontWeight: '600', color: '#1e40af', fontFamily: 'var(--font-family-base)', textAlign: 'center' }}>Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading && backups.length === 0 ? (
                                        <tr>
                                            <td colSpan={4} style={{ textAlign: 'center', padding: '3rem' }}>
                                                <CircularProgress />
                                            </td>
                                        </tr>
                                    ) : backups.length > 0 ? (
                                        backups.map((backup) => (
                                            <tr
                                                key={backup.filename}
                                                style={{
                                                    borderBottom: '1px solid #e5e7eb',
                                                    transition: 'background-color 0.2s ease'
                                                }}
                                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                            >
                                                <td style={{ padding: '1rem', fontWeight: '500', fontFamily: 'var(--font-family-base)' }}>
                                                    <i className="bi bi-file-earmark-zip-fill text-primary me-2"></i>
                                                    {backup.filename}
                                                </td>
                                                <td style={{ padding: '1rem', color: '#64748b', fontFamily: 'var(--font-family-base)' }}>
                                                    {backup.sizeFormatted}
                                                </td>
                                                <td style={{ padding: '1rem', color: '#64748b', fontFamily: 'var(--font-family-base)' }}>
                                                    {new Date(backup.createdAt).toLocaleString('es-AR', {
                                                        year: 'numeric',
                                                        month: '2-digit',
                                                        day: '2-digit',
                                                        hour: '2-digit',
                                                        minute: '2-digit'
                                                    })}
                                                </td>
                                                <td style={{ padding: '1rem', textAlign: 'center' }}>
                                                    <div style={{ display: 'flex', justifyContent: 'center', gap: '8px' }}>
                                                    <button
                                                        className="btn btn-sm"
                                                        onClick={() => openRestoreModal(backup)}
                                                        title="Restaurar backup"
                                                        style={{
                                                            background: '#3B82F6',
                                                            color: 'white',
                                                            border: 'none',
                                                            borderRadius: '6px',
                                                            padding: '0.5rem 1rem',
                                                            fontWeight: '600',
                                                            fontFamily: 'var(--font-family-base)',
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            gap: '6px'
                                                        }}
                                                    >
                                                        <i className="bi bi-arrow-clockwise"></i>
                                                        Restaurar
                                                    </button>
                                                    <button
                                                        className="btn btn-sm"
                                                        onClick={() => openDeleteModal(backup)}
                                                        title="Eliminar backup"
                                                        style={{
                                                            background: '#ef4444',
                                                            color: 'white',
                                                            border: 'none',
                                                            borderRadius: '6px',
                                                            padding: '0.5rem 1rem',
                                                            fontWeight: '600',
                                                            fontFamily: 'var(--font-family-base)',
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            gap: '6px'
                                                        }}
                                                    >
                                                        <i className="bi bi-trash3-fill"></i>
                                                        Eliminar
                                                    </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan={4} style={{ textAlign: 'center', padding: '3rem' }}>
                                                <div style={{ color: '#64748b', fontFamily: 'var(--font-family-base)' }}>
                                                    <i className="bi bi-inbox" style={{ fontSize: '3rem', display: 'block', marginBottom: '1rem', opacity: 0.5 }}></i>
                                                    <Typography>No se encontraron backups.</Typography>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            {/* Modal de confirmación de restauración */}
            <Dialog
                open={showRestoreModal}
                onClose={closeRestoreModal}
                slotProps={{
                    paper: {
                        style: {
                            borderRadius: '12px',
                            padding: '1rem'
                        }
                    }
                }}
            >
                <DialogTitle style={{
                    fontFamily: 'var(--font-family-base)',
                    fontWeight: '600',
                    fontSize: '1.5rem',
                    color: '#1e293b'
                }}>
                    <i className="bi bi-exclamation-triangle-fill text-warning me-2"></i>
                    Confirmar Restauración
                </DialogTitle>
                <DialogContent>
                    <DialogContentText style={{
                        fontFamily: 'var(--font-family-base)',
                        color: '#475569',
                        fontSize: '1rem'
                    }}>
                        ¿Está seguro de que desea restaurar el backup <strong>{selectedBackup?.filename}</strong>?
                        <br /><br />
                        <span style={{ color: '#ef4444', fontWeight: '500' }}>
                            ⚠️ Esta acción sobrescribirá los datos actuales y no se puede deshacer.
                        </span>
                    </DialogContentText>
                </DialogContent>
                <DialogActions style={{ padding: '1rem 1.5rem' }}>
                    <Button
                        onClick={closeRestoreModal}
                        style={{
                            fontFamily: 'var(--font-family-base)',
                            fontWeight: '600',
                            color: '#64748b'
                        }}
                    >
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleRestoreBackup}
                        variant="contained"
                        style={{
                            fontFamily: 'var(--font-family-base)',
                            fontWeight: '600',
                            background: '#ef4444',
                            borderRadius: '8px'
                        }}
                        autoFocus
                    >
                        Confirmar Restauración
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Modal de creación de backup */}
            <CreateBackupModal
                show={showCreateModal}
                onHide={() => setShowCreateModal(false)}
                onCreate={handleCreateBackup}
            />

            {/* Modal de configuración de backup */}
            <ConfigBackupModal
                show={showConfigModal}
                onHide={() => setShowConfigModal(false)}
                config={backupConfig}
                onSave={handleSaveConfig}
            />

            {/* Modal de confirmación de eliminación */}
            <Dialog
                open={showDeleteModal}
                onClose={closeDeleteModal}
                slotProps={{
                    paper: {
                        style: {
                            borderRadius: '12px',
                            padding: '1rem'
                        }
                    }
                }}
            >
                <DialogTitle style={{
                    fontFamily: 'var(--font-family-base)',
                    fontWeight: '600',
                    fontSize: '1.5rem',
                    color: '#1e293b'
                }}>
                    <i className="bi bi-trash3-fill text-danger me-2"></i>
                    Confirmar Eliminación
                </DialogTitle>
                <DialogContent>
                    <DialogContentText style={{
                        fontFamily: 'var(--font-family-base)',
                        color: '#475569',
                        fontSize: '1rem'
                    }}>
                        ¿Está seguro de que desea eliminar el backup <strong>{deletingBackup?.filename}</strong>?
                        <br /><br />
                        <span style={{ color: '#ef4444', fontWeight: '500' }}>
                            ⚠️ Esta acción es permanente y no se puede deshacer.
                        </span>
                    </DialogContentText>
                </DialogContent>
                <DialogActions style={{ padding: '1rem 1.5rem' }}>
                    <Button
                        onClick={closeDeleteModal}
                        style={{
                            fontFamily: 'var(--font-family-base)',
                            fontWeight: '600',
                            color: '#64748b'
                        }}
                    >
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleDeleteBackup}
                        variant="contained"
                        style={{
                            fontFamily: 'var(--font-family-base)',
                            fontWeight: '600',
                            background: '#ef4444',
                            borderRadius: '8px'
                        }}
                        autoFocus
                    >
                        Eliminar
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar para notificaciones */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                sx={{ zIndex: 10000 }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    sx={{
                        width: '100%',
                        fontFamily: 'var(--font-family-base)',
                        fontWeight: '500',
                        borderRadius: '8px',
                        boxShadow: 'var(--shadow-md)'
                    }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </div>
    );
};

export default GestionBackups;
